/**
 * FocusDeployButton — the "Turn on" affordance for a dashboard focus row whose
 * fix is a Pulse prescription (a draft flow to activate, a worker to deploy, a
 * bottleneck to queue).
 *
 * Activating an autonomous flow ("runs end-to-end on your schedule") is
 * consequential, so the button does NOT fire on click — it opens a confirmation
 * modal that first DESCRIBES what will happen: the flow's summary, its step chain
 * (what it actually does, and which worker role runs each step), what it replaces,
 * and how it's governed (the ship/verify gate). Only the modal's confirm button
 * POSTs the EXISTING `pulse.prescribe` route — the same activation path the Pulse
 * wall/board use (Pulse forks no second activation path). The preview is fetched
 * lazily (only when the modal opens) via `pulse.prescribePreview`, so a dashboard
 * render never pays for detail the user hasn't asked to see.
 *
 * `FocusSections` is server-rendered, so a successful activation refreshes via
 * `router.refresh()` (re-running the assembler that no longer lists the now-active
 * flow) plus a best-effort revalidation of the live Pulse SWR keys. A `{ok:false}`
 * with an `href` follows that surface (the graceful fallback the route returns
 * when a wiring is risky/uncertain).
 */

import * as React from "react";
import { useNavigate as useRouter } from "../../router";
import { useSWRConfig } from "swr";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../dialog";
import { Button } from "../../button";
import { Badge } from "../../badge";
import { Icon } from "../../icon";
import { Link } from "../link";
import { apiCall, useApi } from "../../api/client";
import { pulse } from "../../api/contracts/pulse";
import type { FocusDeploy } from "./focus-list";

export function FocusDeployButton({
  deploy,
  label,
}: {
  deploy: FocusDeploy;
  /** The verb from the prescription cta ("Turn on" / "Optimize"). */
  label: string;
}) {
  const router = useRouter();
  const { mutate } = useSWRConfig();
  const [open, setOpen] = React.useState(false);
  const [pending, startTransition] = React.useTransition();
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Fetch the "what will this do" preview only once the modal is open.
  const { data: preview, isLoading } = useApi(
    pulse.prescribePreview,
    open ? { query: { kind: deploy.kind, ref: deploy.ref } } : null,
  );

  const busyLabel = label === "Turn on" ? "Turning on…" : "Working…";
  const doneLabel = label === "Turn on" ? "On" : "Done";
  const title = preview?.title || deploy.title;
  const blocked = preview?.found === false;

  const confirm = () => {
    if (pending || done) return;
    setError(null);
    startTransition(async () => {
      try {
        const res = await apiCall(pulse.prescribe, {
          body: {
            kind: deploy.kind,
            mode: deploy.mode,
            groupKey: deploy.groupKey,
            touchId: deploy.touchId,
            title: deploy.title,
            ref: deploy.ref,
            evidence: deploy.evidence,
          },
        });
        if (!res.ok) {
          // A risky/uncertain wiring falls back to the surface it returned, so
          // the user still lands somewhere actionable.
          if (res.href) {
            router.push(res.href);
            setOpen(false);
          } else {
            setError("Couldn't turn it on — try again.");
          }
          return;
        }
        setDone(true);
        setOpen(false);
        router.refresh();
        await mutate(
          (key) => typeof key === "string" && key.startsWith("/api/pulse"),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't turn it on.");
      }
    });
  };

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        disabled={done}
        onClick={() => setOpen(true)}
        className="shrink-0"
      >
        {done ? doneLabel : label}
      </Button>

      <Dialog open={open} onOpenChange={(o) => !pending && setOpen(o)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{label} “{title}”?</DialogTitle>
            {preview?.summary ? (
              <DialogDescription>{preview.summary}</DialogDescription>
            ) : (
              <DialogDescription>
                Review what this does before it runs.
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="flex flex-col gap-4 py-1">
            {isLoading && !preview ? (
              <PreviewSkeleton />
            ) : (
              <>
                {preview && preview.steps.length > 0 ? (
                  <section className="flex flex-col gap-2">
                    <h4 className="type-subtext font-medium text-muted-foreground">
                      What it does
                      {preview.stepCount > 0 ? (
                        <span className="text-muted-foreground/60">
                          {" "}
                          · {preview.stepCount} step
                          {preview.stepCount === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </h4>
                    <ol className="flex flex-col gap-1.5">
                      {preview.steps.map((s, i) => (
                        <li key={i} className="flex items-center gap-2.5 type-body">
                          <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted type-subtext tabular-nums text-muted-foreground">
                            {i + 1}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-foreground">
                            {s.label}
                          </span>
                          {s.role ? (
                            <Badge variant="neutral" className="shrink-0">
                              {s.role}
                            </Badge>
                          ) : null}
                        </li>
                      ))}
                    </ol>
                  </section>
                ) : null}

                {preview?.replaces ? (
                  <section className="flex items-start gap-2 type-body">
                    <Icon
                      name="repeat"
                      size="sm"
                      className="mt-0.5 shrink-0 text-muted-foreground"
                    />
                    <p className="text-muted-foreground">
                      <span className="text-foreground">Replaces</span>{" "}
                      {preview.replaces}
                    </p>
                  </section>
                ) : null}

                {preview?.effect ? (
                  <section className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/30 px-3 py-2.5 type-body">
                    <Icon
                      name={blocked ? "triangle-alert" : "info"}
                      size="sm"
                      className={
                        blocked
                          ? "mt-0.5 shrink-0 text-warning"
                          : "mt-0.5 shrink-0 text-muted-foreground"
                      }
                    />
                    <p className="text-muted-foreground">{preview.effect}</p>
                  </section>
                ) : null}

                {error ? (
                  <p className="type-body text-destructive">{error}</p>
                ) : null}
              </>
            )}
          </div>

          <DialogFooter className="sm:justify-between">
            {preview?.detailHref ? (
              <Button asChild variant="ghost" size="sm">
                <Link href={preview.detailHref} variant="unstyled">
                  View full details
                  <Icon name="arrow-up-right" size="sm" />
                </Link>
              </Button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={confirm} disabled={pending || blocked}>
                {pending ? (
                  <Icon name="loader2" size="sm" className="animate-spin" />
                ) : null}
                {pending ? busyLabel : label}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PreviewSkeleton() {
  return (
    <div className="flex flex-col gap-2.5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-2.5">
          <span className="size-5 shrink-0 animate-pulse rounded-full bg-muted/50" />
          <span
            className="h-3 animate-pulse rounded bg-muted/40"
            style={{ width: `${70 - i * 12}%` }}
          />
        </div>
      ))}
    </div>
  );
}
